import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseDashboard } from '../../models/dashboard/BaseDashboard';
import { FormSearch } from '../../models/dashboard/FormStatus';
import { UtilityService } from '../utility.service';
import { NotificationService } from '../notification.service';
import { OpenClosePeriodFormView } from 'app/models/general-ledger/OpenClosePeriodFormView';
import { FinancialPeriodView } from 'app/models/general-ledger/FinancialPeriodView';
declare const moment: any;

@Injectable({
	providedIn: 'root',
})
export class OpenClosePeriodService {
	headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0' });

	private readonly urlPath = `${environment.apiUrl}general-ledger/clients/`;

	constructor(
		private httpClient: HttpClient,
		private utilityService: UtilityService,
		private notificationService: NotificationService
	) {}

	createForm(clientId: string, request: any): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/open-close-period`, this.setupRequest(request))
			.pipe();
	}

	private setupRequest(request: any) {
		const formData: FormData = new FormData();

		formData.append('model', JSON.stringify(request));
		return formData;
	}

	updateForm(clientId: string, id: string, request: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/forms/open-close-period/${id}`, this.setupRequest(request))
			.pipe();
	}
	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/forms/open-close-period/${formId}/docs/${docId}`);
	}
	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/forms/open-close-period/${id}`).pipe();
	}

	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/open-close-period/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/open-close-period/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${
			this.urlPath
		}${clientId}/forms/open-close-period/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/forms/open-close-period/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/forms/open-close-period/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/forms/open-close-period/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<OpenClosePeriodFormView> {
		return this.httpClient
			.post<OpenClosePeriodFormView>(`${this.urlPath}${clientId}/forms/open-close-period/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	getOpenClosePeriods(clientId: string): Observable<FinancialPeriodView[]> {
		let url = `${this.urlPath}${clientId}/forms/open-close-period/get-periods`;
		return this.httpClient.get<FinancialPeriodView[]>(url, { headers: this.headers }).pipe();
	}

	GetAvailableYears(clientId: string, type: string): Observable<number> {
		let url = `${this.urlPath}${clientId}/forms/open-close-period/${type}/available-years`;
		return this.httpClient.get<number>(url, { headers: this.headers }).pipe();
	}

	GetAvailableMonths(clientId: string, type: string, period: number): Observable<number> {
		let url = `${this.urlPath}${clientId}/forms/open-close-period/${type}/${period}/available-months`;
		return this.httpClient.get<number>(url, { headers: this.headers }).pipe();
	}

	HasDepreciationModule(clientId: string): Observable<any> {
		let url = `${this.urlPath}${clientId}/forms/open-close-period/has-depreciation-module`;
		return this.httpClient.get<any>(url, { headers: this.headers }).pipe();
	}
	getOpenPeriods(clientId: string): Observable<FinancialPeriodView[]> {
		return this.getOpenClosePeriods(clientId).pipe(
			map((data) => {
				return (data || []).filter((period: FinancialPeriodView) => period && period.closeDate == null);
			}),
			catchError((error) => {
				console.error('Error loading financial periods:', error);
				this.notificationService.showError('Unable to load financial periods.', 'Error');
				return of([]);
			})
		);
	}
	isPeriodOpen(periodLists: FinancialPeriodView[], month: number, year: number): boolean {
		if (!periodLists || periodLists.length === 0) return false;
		return periodLists.some((period) => period.month === month && period.year === year);
	}

	private openPeriodsCache: FinancialPeriodView[] = [];

	loadOpenPeriodsCache(clientId: string): void {
		this.getOpenPeriods(clientId).subscribe((periods) => {
			this.openPeriodsCache = periods;
		});
	}

	getClosedPeriodErrorForDate(dateVal: string, fieldLabel: string): string | null {
		if (!dateVal || !moment(dateVal, 'MM/DD/YYYY', true).isValid()) return null;
		const parsedDate = moment(dateVal, 'MM/DD/YYYY');
		const month = parsedDate.month() + 1;
		const year = this.utilityService.getFiscalYearFromDate(parsedDate.toDate());
		if (!this.isPeriodOpen(this.openPeriodsCache, month, year)) {
			const monthYear = `${parsedDate.format('MMMM')} FY ${year}`;
			return `The ${fieldLabel} ${dateVal} falls within a closed period (${monthYear}). Please update the ${fieldLabel} to an open period or contact your administrator to reopen the period.`;
		}
		return null;
	}

	validateEffectiveDatePeriod(periodLists: FinancialPeriodView[], effectiveDateValue: any): boolean {
		if (!effectiveDateValue) {
			return true;
		}

		const effectiveDate = moment(effectiveDateValue);
		if (!effectiveDate.isValid()) {
			return true;
		}

		const month = effectiveDate.month() + 1;
		const year = this.utilityService.getFiscalYearFromDate(effectiveDate);
		if (!this.isPeriodOpen(periodLists, month, year)) {
			this.notificationService.showError(
				'Selected Effective Date is not in open financial period.',
				'Validation Error'
			);
			return false;
		}
		return true;
	}
}
